import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gold-500/20 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-gold-500" />
              </div>
              <div>
                <h1 className="font-display text-4xl">Terms of Service</h1>
                <p className="text-slate-500">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-invert prose-gold max-w-none">
              <div className="bg-charcoal border border-slate-800 rounded-sm p-8 mb-8">
                <p className="text-slate-300 leading-relaxed">
                  Welcome to ExpertBase. By accessing or using our platform, you agree to be bound by these Terms of Service. 
                  Please read them carefully before using our services.
                </p>
              </div>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">1. Acceptance of Terms</h2>
                <div className="space-y-4 text-slate-300">
                  <p>
                    By creating an account or using ExpertBase, you agree to these Terms of Service and our Privacy Policy. 
                    If you do not agree, please do not use our platform.
                  </p>
                  <p>
                    We reserve the right to modify these terms at any time. Continued use of the platform after changes 
                    constitutes acceptance of the modified terms.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">2. Description of Services</h2>
                <div className="space-y-4 text-slate-300">
                  <p>ExpertBase provides:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Expert Consultations:</strong> A platform connecting users with industry experts for paid 1-on-1 consultation sessions.</li>
                    <li><strong>GTM Team Building:</strong> Services to help businesses build go-to-market teams in new regions.</li>
                    <li><strong>Enterprise Solutions:</strong> Custom expert networks and dedicated support for organizations.</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">3. User Accounts</h2>
                <div className="space-y-4 text-slate-300">
                  <h3 className="text-lg font-semibold text-white">Registration</h3>
                  <p>To use certain features, you must create an account. You agree to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Promptly update any changes to your information</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6">Expert Accounts</h3>
                  <p>If you register as an expert, you additionally agree to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide truthful information about your qualifications and experience</li>
                    <li>Maintain professional conduct during all interactions</li>
                    <li>Honor scheduled sessions and commitments</li>
                    <li>Not share confidential information disclosed by clients</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">4. Payments and Fees</h2>
                <div className="space-y-4 text-slate-300">
                  <h3 className="text-lg font-semibold text-white">For Users</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>All payments are processed securely through Stripe</li>
                    <li>Fees are displayed before booking confirmation</li>
                    <li>Payments are charged at the time of booking</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6">For Experts</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Experts set their own hourly rates</li>
                    <li>ExpertBase retains a platform fee (disclosed during registration)</li>
                    <li>Payouts are processed according to the payout schedule</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6">Refunds</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Cancellations made 24+ hours before a session: Full refund</li>
                    <li>Cancellations made less than 24 hours: 50% refund</li>
                    <li>No-shows by users: No refund</li>
                    <li>No-shows by experts: Full refund plus account credit</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">5. Code of Conduct</h2>
                <div className="space-y-4 text-slate-300">
                  <p>All users must:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Treat others with respect and professionalism</li>
                    <li>Not engage in harassment, discrimination, or abusive behavior</li>
                    <li>Not use the platform for illegal activities</li>
                    <li>Not attempt to circumvent platform fees or payments</li>
                    <li>Not share account credentials with others</li>
                    <li>Not scrape, copy, or misuse platform data</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">6. Intellectual Property</h2>
                <div className="space-y-4 text-slate-300">
                  <p>
                    The ExpertBase platform, including its design, features, and content, is owned by ExpertBase and protected 
                    by intellectual property laws. You may not copy, modify, distribute, or create derivative works without 
                    our express written permission.
                  </p>
                  <p>
                    Content you create (profiles, reviews, etc.) remains yours, but you grant ExpertBase a license to use, 
                    display, and distribute it on the platform.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">7. Disclaimers</h2>
                <div className="space-y-4 text-slate-300">
                  <ul className="list-disc pl-6 space-y-2">
                    <li>ExpertBase is a platform connecting users with experts. We do not guarantee the quality, accuracy, or outcomes of any consultation.</li>
                    <li>Expert advice is provided for informational purposes and should not be considered professional advice (legal, financial, medical, etc.).</li>
                    <li>We are not responsible for the actions or conduct of any user or expert.</li>
                    <li>The platform is provided "as is" without warranties of any kind.</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">8. Limitation of Liability</h2>
                <div className="space-y-4 text-slate-300">
                  <p>
                    To the maximum extent permitted by law, ExpertBase shall not be liable for any indirect, incidental, 
                    special, consequential, or punitive damages arising from your use of the platform.
                  </p>
                  <p>
                    Our total liability shall not exceed the amount you paid to ExpertBase in the 12 months preceding the claim.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">9. Termination</h2>
                <div className="space-y-4 text-slate-300">
                  <p>
                    We may suspend or terminate your account if you violate these terms or engage in behavior that harms 
                    our platform, users, or reputation. You may also delete your account at any time.
                  </p>
                  <p>
                    Upon termination, your right to use the platform ceases immediately. Certain provisions of these terms 
                    survive termination.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">10. Governing Law</h2>
                <div className="space-y-4 text-slate-300">
                  <p>
                    These Terms of Service are governed by the laws of the United Arab Emirates. Any disputes shall be 
                    resolved in the courts of Dubai, UAE.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">11. Contact Us</h2>
                <div className="space-y-4 text-slate-300">
                  <p>If you have any questions about these Terms of Service, please contact us:</p>
                  <div className="bg-obsidian border border-slate-800 p-6 rounded-sm mt-4">
                    <p><strong>ExpertBase</strong></p>
                    <p>Dubai, UAE</p>
                    <p>Email: <a href="mailto:legal@expertbase.ai" className="text-gold-500 hover:text-gold-400">legal@expertbase.ai</a></p>
                  </div>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
